import React from 'react';
import { Form, Card, Button, Spinner } from 'react-bootstrap';
import { useUploadFile } from '../../../../../api/fileUpload/fileUpload.hooks';

const SECTION_ID = 'MEDIA GALLERY';

const MediaGalleryEditor = ({ mediaGallery, onChange }) => {
  const { upload, isLoading: isUploading } = useUploadFile();

  // Helper to ensure id is always included in updates
  const createUpdate = (updates) => ({
    ...mediaGallery,
    id: SECTION_ID,
    ...updates
  });

  const handleChange = (field, value) => {
    onChange(createUpdate({ [field]: value }));
  };

  // Video URL handlers
  const handleVideoChange = (index, value) => {
    const newVideos = [...(mediaGallery?.videos || [])];
    newVideos[index] = value;
    onChange(createUpdate({ videos: newVideos }));
  };

  const addVideo = () => {
    onChange(createUpdate({
      videos: [...(mediaGallery?.videos || []), '']
    }));
  };

  const removeVideo = (index) => {
    const newVideos = (mediaGallery?.videos || []).filter((_, i) => i !== index);
    onChange(createUpdate({ videos: newVideos }));
  };

  // Image handlers
  const handleImageChange = (index, value) => {
    const newImages = [...(mediaGallery?.images || [])];
    newImages[index] = value;
    onChange(createUpdate({ images: newImages }));
  };

  const handleImageUpload = async (index, e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const result = await upload(file);
      handleImageChange(index, result.url);
    } catch (err) {
      console.error('Upload failed:', err);
    }
  };

  const addImage = () => {
    onChange(createUpdate({
      images: [...(mediaGallery?.images || []), '']
    }));
  };

  const removeImage = (index) => {
    const newImages = (mediaGallery?.images || []).filter((_, i) => i !== index);
    onChange(createUpdate({ images: newImages }));
  };

  return (
    <Card className="mb-3">
      <Card.Header className='bg-neutral-gray'>
        <h5 className="mb-0">Media Gallery</h5>
      </Card.Header>
      <Card.Body>
        {/* Heading */}
        <Form.Group className="mb-4">
          <Form.Label>Heading</Form.Label>
          <Form.Control
            type="text"
            value={mediaGallery?.heading || ''}
            onChange={(e) => handleChange('heading', e.target.value)}
            placeholder="Enter gallery heading"
          />
        </Form.Group>

        {/* Videos Section */}
        <div className="mb-4">
          <h6 className="mb-3">Videos</h6>
          {(mediaGallery?.videos || []).map((video, index) => (
            <div key={index} className="mb-3 p-3 border rounded">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <strong>Video {index + 1}</strong>
                <Button variant="outline-danger" size="sm" onClick={() => removeVideo(index)}>
                  <i className="fa fa-trash"></i>
                </Button>
              </div>
              <Form.Group>
                <Form.Label>Video URL</Form.Label>
                <Form.Control
                  type="text"
                  value={video}
                  onChange={(e) => handleVideoChange(index, e.target.value)}
                  placeholder="Enter video URL (e.g., YouTube, Vimeo)"
                />
              </Form.Group>
              {video && (
                <div className="mt-2">
                  <small className="text-muted">Preview URL: {video}</small>
                </div>
              )}
            </div>
          ))}
          <Button variant="outline-primary" size="sm" onClick={addVideo}>
            <i className="fa fa-plus me-1"></i> Add Video
          </Button>
        </div>

        {/* Images Section */}
        <div>
          <h6 className="mb-3">Images</h6>
          <div className="row">
            {(mediaGallery?.images || []).map((image, index) => (
              <div key={index} className="col-md-4 mb-3">
                <div className="p-3 border rounded h-100">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <strong>Image {index + 1}</strong>
                    <Button variant="outline-danger" size="sm" onClick={() => removeImage(index)}>
                      <i className="fa fa-trash"></i>
                    </Button>
                  </div>
                  <div>
                    {image ? (
                      <div className="position-relative d-inline-block">
                        <img
                          src={image}
                          alt={`Gallery image ${index + 1}`}
                          style={{ width: '100%', maxWidth: '200px', height: '150px', objectFit: 'cover', borderRadius: '4px', border: '1px solid #dee2e6' }}
                        />
                        <Button
                          variant="danger"
                          size="sm"
                          className="position-absolute top-0 end-0"
                          style={{ transform: 'translate(50%, -50%)' }}
                          onClick={() => handleImageChange(index, '')}
                        >
                          <i className="fa fa-times"></i>
                        </Button>
                      </div>
                    ) : (
                      <Button
                        variant="outline-primary"
                        as="label"
                        disabled={isUploading}
                      >
                        {isUploading ? (
                          <>
                            <Spinner animation="border" size="sm" className="me-1" />
                            Uploading...
                          </>
                        ) : (
                          <>
                            <i className="fa fa-upload me-1"></i> Upload Image
                          </>
                        )}
                        <input
                          type="file"
                          hidden
                          accept="image/*"
                          onChange={(e) => handleImageUpload(index, e)}
                        />
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <Button variant="outline-primary" size="sm" onClick={addImage}>
            <i className="fa fa-plus me-1"></i> Add Image
          </Button>
        </div>
      </Card.Body>
    </Card>
  );
};

export default MediaGalleryEditor;

